// Add new sidebar navigation
import React, { useState } from 'react';
import type { NavigationItem, NavigationSection, User } from '../../types';

interface MainLayoutProps {
    currentSection: NavigationSection;
    currentUser: User | null;
    children: React.ReactNode;
    onSectionChange: (section: NavigationSection) => void;
    onLogout: () => void;
    navigationItems: NavigationItem[];
    displayVersion: string | null;
}

const Sidebar: React.FC<{
    currentSection: NavigationSection;
    navigationItems: NavigationItem[];
    onSectionChange: (section: NavigationSection) => void;
    isCollapsed: boolean;
    onCollapse: () => void;
    currentUser: User | null;
    onLogout: () => void;
    displayVersion: string | null;
}> = ({ currentSection, navigationItems, onSectionChange, isCollapsed, onCollapse, currentUser, onLogout, displayVersion }) => {
    const [expandedGroups, setExpandedGroups] = useState<string[]>(() =>
        navigationItems
            .filter(item => item.children?.some(child => child.key === currentSection))
            .map(item => item.label)
    );

    const getIcon = (section: NavigationSection): string => {
        switch (section) {
            case 'integrations': return 'bi-puzzle-fill';
            case 'suppliers': return 'bi-truck';
            case 'users': return 'bi-people-fill';
            case 'diagnostics': return 'bi-clipboard2-pulse';
            case 'clients': return 'bi-person-lines-fill';
            case 'categories': return 'bi-tag';
            case 'products': return 'bi-box-seam';
            case 'attributes': return 'bi-sliders';
            case 'b2b-clients': return 'bi-key-fill';
            case 'shop-companies': return 'bi-building';
            case 'shop-orders': return 'bi-receipt';
            case 'maintenance': return 'bi-tools';
            default: return 'bi-circle-fill';
        }
    };

    const isGroupActive = (item: NavigationItem): boolean =>
        Boolean(item.children?.some(child => child.key === currentSection));

    const toggleGroup = (label: string) => {
        setExpandedGroups(prev => prev.includes(label)
            ? prev.filter(name => name !== label)
            : [...prev, label]);
    };

    const handleGroupClick = (item: NavigationItem) => {
        if (isCollapsed) {
            if (!expandedGroups.includes(item.label)) {
                setExpandedGroups(prev => [...prev, item.label]);
            }
            onCollapse();
            return;
        }
        toggleGroup(item.label);
    };

    return (
        <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
            <div className="sidebar-brand">
                <div className="navbar-brand-custom mb-4">
                    <i className="bi bi-shop"></i>
                    {!isCollapsed && <span className="brand-text">Lira</span>}
                </div>
                {isCollapsed && (
                    <button
                        className="sidebar-collapse collapsed-button"
                        onClick={onCollapse}
                        title="Развернуть панель"
                    >
                        <i className="bi bi-chevron-right"></i>
                    </button>
                )}
                {!isCollapsed && (
                    <button
                        className="sidebar-collapse"
                        onClick={onCollapse}
                        title="Свернуть панель"
                    >
                        <i className="bi bi-chevron-left"></i>
                    </button>
                )}
            </div>
            <nav className="sidebar-nav">
                {navigationItems.map(item =>
                    item.children ? (
                        <div key={item.label} className="sidebar-nav-group">
                            <button
                                className={`sidebar-nav-item sidebar-nav-group-toggle ${isGroupActive(item) ? 'has-active-child' : ''} ${isCollapsed ? 'collapsed' : ''}`}
                                onClick={() => handleGroupClick(item)}
                                title={isCollapsed || !expandedGroups.includes(item.label) ? item.label : ''}
                            >
                                <i className={`bi ${item.icon ?? 'bi-folder-fill'} ${!isCollapsed ? 'mr-2' : ''}`}></i>
                                {!isCollapsed && <span>{item.label}</span>}
                            </button>
                            {!isCollapsed && expandedGroups.includes(item.label) && (
                                <div className="sidebar-subnav">
                                    {item.children.map(child => (
                                        <button
                                            key={child.key}
                                            className={`sidebar-subnav-item ${currentSection === child.key ? 'active' : ''}`}
                                            onClick={() => onSectionChange(child.key)}
                                            title={child.label}
                                        >
                                            {child.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <button
                            key={item.key}
                            className={`sidebar-nav-item ${currentSection === item.key ? 'active' : ''} ${isCollapsed ? 'collapsed' : ''}`}
                            onClick={() => onSectionChange(item.key!)}
                            title={isCollapsed ? item.label : ''}
                        >
                            <i className={`bi ${getIcon(item.key!)} ${!isCollapsed ? 'mr-2' : ''}`}></i>
                            {!isCollapsed && <span>{item.label}</span>}
                        </button>
                    )
                )}
            </nav>
            <div className="sidebar-footer">
                <div className="user-info">
                    <div className="user-details">
                        <span className="user-username">
                            <strong>{currentUser?.username}</strong>
                        </span>
                        {!isCollapsed && displayVersion && (
                            <span className="app-version">{displayVersion}</span>
                        )}
                    </div>
                    <button
                        className="btn btn-sm sidebar-logout"
                        onClick={onLogout}
                        title="Выход"
                    >
                        <i className="bi bi-box-arrow-right"></i>
                    </button>
                </div>
            </div>
        </div>
    );
};

export const MainLayout: React.FC<MainLayoutProps> = ({
    currentSection,
    currentUser,
    children,
    onSectionChange,
    onLogout,
    navigationItems,
    displayVersion
}) => {
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

    const collapseSidebar = () => {
        setIsSidebarCollapsed(!isSidebarCollapsed);
    };

    const handleSectionChange = (section: NavigationSection) => {
        onSectionChange(section);
    };

    return (
        <div className="main-layout">
            <Sidebar
                currentSection={currentSection}
                navigationItems={navigationItems}
                onSectionChange={handleSectionChange}
                isCollapsed={isSidebarCollapsed}
                onCollapse={collapseSidebar}
                currentUser={currentUser}
                onLogout={onLogout}
                displayVersion={displayVersion}
            />
            <div className={`main-content ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
                <main className="main-body">
                    {children}
                </main>
            </div>
        </div>
    );
};
